import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {OptimizationCardScratchpadState} from '../../scratchpad/optimizationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const setOptimizationGoalInputSchema = z.object({
  goal: z
    .number()
    .int({message: 'Goal must be a whole number'})
    .min(1, {message: 'Optimization goal must be at least 1'})
    .describe(
      'The number of variants to serve in the end. Optimization will gradually drop underperforming variants until this many remain, then optimize between them.',
    ),
});

export default function setOptimizationGoalTool() {
  async function execute(
    params: ToolExecuteParams<
      typeof setOptimizationGoalInputSchema,
      OptimizationCardScratchpadState
    >,
  ) {
    const {goal} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<OptimizationCardScratchpadState>(
      agentScratchpad,
      NodeType.GENERAL_OPTIMIZATION,
    );

    const outputsCount = validatedScratchpad.card.outputs.length;

    if (goal > outputsCount) {
      return toolError(
        `Optimization goal (${goal}) cannot exceed the number of outputs (${outputsCount}). Use ${V2CardAgentToolName.AddOrUpdateOptimizationCard} to add more outputs first.`,
      );
    }

    validatedScratchpad.card.configuration = {
      ...validatedScratchpad.card.configuration,
      entitiesToShowGoal: goal,
    };

    return toolResult(
      `Optimization goal set to ${goal}. Optimization will drop underperforming variants until ${goal} remain, then optimize between them.`,
    );
  }

  return tool<typeof setOptimizationGoalInputSchema, OptimizationCardScratchpadState>({
    name: V2CardAgentToolName.SetOptimizationGoal,
    displayName: 'Setting optimization goal',
    description:
      'Set how many variants to serve in the end. Optimization gradually drops underperforming variants until this goal is reached, then optimizes between the remaining variants.',
    inputSchema: setOptimizationGoalInputSchema,
    execute,
  });
}
